// @ts-nocheck
import { Fragment, useEffect, useRef, useMemo, useState, useCallback } from "react";
import { useChatWebSocket } from "@/hooks/use-chat-websocket";
import { usePaginatedMessages } from "@/hooks/use-paginated-messages";
import { TypingIndicator } from "./typing-indicator";

const CLAIM_QUICK_ACTIONS = [
  { label: "Summarize", prompt: "Summarize this claim — current status, key dates, and outstanding items." },
  { label: "Next Steps", prompt: "What are the recommended next steps for this claim?" },
  { label: "Document Review", prompt: "Review the documents on file and highlight anything missing or critical." },
];

const GENERIC_QUICK_ACTIONS = [
  { label: "Claims Overview", prompt: "Give me an overview of my active claims portfolio." },
  { label: "Stale Claims", prompt: "Which claims have had no recent activity and need attention?" },
  { label: "High Value", prompt: "List my highest value open claims and their current status." },
];
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/query-client";
import { useFloatingChat } from "@/contexts/floating-chat-context";
import { MessageBubble } from "./message-bubble";
import { MentionInput } from "./mention-input";
import { ChatDateSeparator } from "./chat-date-separator";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Minus, X, Maximize2, Maximize, Minimize, RefreshCw, AlertTriangle, Brain, Square } from "lucide-react";
import { formatMessageDate, getChatSpaceDisplayName } from "@/lib/chat-utils";

interface FloatingChatWindowProps {
  spaceId: string;
  index: number;
  isMinimized: boolean;
  currentUserId?: string;
  popupMode?: boolean;
}

export function FloatingChatWindow({ spaceId, index, isMinimized, currentUserId, popupMode }: FloatingChatWindowProps) {
  const { closeChat, toggleMinimize } = useFloatingChat();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const topSentinelRef = useRef<HTMLDivElement>(null);
  const unreadBannerRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Track whether user has manually scrolled up (suppress auto-scroll-to-bottom)
  const userScrolledUpRef = useRef(false);
  const prevScrollHeightRef = useRef(0);
  const initialScrollDone = useRef(false);

  const { data: space } = useQuery({
    queryKey: [`/api/chat/spaces/${encodeURIComponent(spaceId)}`],
  });

  const [localUnread, setLocalUnread] = useState(0);

  const { data: participantsData } = useQuery({
    queryKey: [`/api/chat/spaces/${encodeURIComponent(spaceId)}/participants`],
    enabled: !isMinimized,
  });
  const participants = participantsData?.participants || [];

  // Find the current user's lastReadAt from participants
  const myLastReadAt = useMemo(() => {
    const me = participants.find((p: any) => p.userId === currentUserId || p.user?.id === currentUserId);
    return me?.lastReadAt ?? null;
  }, [participants, currentUserId]);

  const {
    messages,
    hasOlder,
    isLoadingInitial,
    isLoadingOlder,
    loadOlder,
    unreadAnchorIndex,
    appendMessage,
    updateMessage,
    pollNow,
  } = usePaginatedMessages({
    spaceId,
    enabled: !isMinimized && participants.length > 0,
    lastReadAt: myLastReadAt,
    pollInterval: 10000,
  });

  const hasUnknown = participants.some((p: any) => {
    if (p.isAgent) return false;
    const name = `${p.user?.firstName || ""} ${p.user?.lastName || ""}`.trim();
    return p.name === "Unknown" || name === "" || name === "Unknown";
  });

  const [pendingMessages, setPendingMessages] = useState<Array<{
    id: string;
    text: string;
    fileNames?: string[];
    files?: File[];
    mentions?: Array<{ userId: string; offset: number; length: number }>;
    status: 'sending' | 'uploading' | 'failed';
    error?: string;
  }>>([]);

  const sendMessageMutation = useMutation({
    mutationFn: async (data: { text: string; mentions: Array<{ userId: string; offset: number; length: number }>; files?: File[] }) => {
      let attachments: Array<{ fileName: string; fileUrl: string; storageKey: string; mimeType: string; size: number }> | undefined;

      if (data.files && data.files.length > 0) {
        const formData = new FormData();
        data.files.forEach(f => formData.append('files', f));
        const uploadRes = await apiRequest('POST', `/api/chat/spaces/${encodeURIComponent(spaceId)}/attachments`, formData);
        const uploadData = await uploadRes.json();
        attachments = uploadData.attachments;
      }

      const response = await apiRequest("POST", `/api/chat/spaces/${encodeURIComponent(spaceId)}/messages`, {
        text: data.text,
        mentions: data.mentions.length > 0 ? data.mentions : undefined,
        attachments,
      });
      return response.json();
    },
  });

  const handleSend = useCallback((data: { text: string; mentions?: Array<{ userId: string; offset: number; length: number }>; files?: File[] }) => {
    const tempId = `pending-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const hasFiles = data.files && data.files.length > 0;
    setPendingMessages(prev => [...prev, {
      id: tempId,
      text: data.text,
      fileNames: data.files?.map(f => f.name),
      files: data.files,
      mentions: data.mentions,
      status: hasFiles ? 'uploading' : 'sending',
    }]);

    // User initiated a send — treat as "at bottom", always scroll to show sent message
    userScrolledUpRef.current = false;

    // Start bot typing indicator timer for agent DMs
    if (isAgentDMRef.current) {
      setBotTimedOut(false);
      if (typingDelayRef.current) clearTimeout(typingDelayRef.current);
      if (botTimeoutRef.current) clearTimeout(botTimeoutRef.current);
      typingDelayRef.current = setTimeout(() => {
        setWaitingForBot(true);
        botTimeoutRef.current = setTimeout(() => {
          setWaitingForBot(false);
          setBotTimedOut(true);
        }, 90_000);
      }, 1000);
    }

    sendMessageMutation.mutate(
      { text: data.text, mentions: data.mentions ?? [], files: data.files },
      {
        onSuccess: (confirmedMessage) => {
          appendMessage(confirmedMessage);
          setPendingMessages(prev => prev.filter(m => m.id !== tempId));
          queryClient.invalidateQueries({ queryKey: ["/api/chat/spaces"] });
          queryClient.invalidateQueries({ queryKey: ["/api/chat/unread-count"] });
        },
        onError: (error: any) => {
          setPendingMessages(prev => prev.map(m =>
            m.id === tempId ? { ...m, status: 'failed', error: error.message || "Failed to send message" } : m
          ));
        },
      }
    );
  }, [spaceId, sendMessageMutation, appendMessage]);

  const retryMessage = useCallback((pending: typeof pendingMessages[0]) => {
    const hasFiles = pending.files && pending.files.length > 0;
    setPendingMessages(prev => prev.map(m =>
      m.id === pending.id ? { ...m, status: hasFiles ? 'uploading' : 'sending', error: undefined } : m
    ));
    sendMessageMutation.mutate(
      { text: pending.text, mentions: pending.mentions ?? [], files: pending.files },
      {
        onSuccess: (confirmedMessage) => {
          appendMessage(confirmedMessage);
          setPendingMessages(prev => prev.filter(m => m.id !== pending.id));
          queryClient.invalidateQueries({ queryKey: ["/api/chat/spaces"] });
          queryClient.invalidateQueries({ queryKey: ["/api/chat/unread-count"] });
        },
        onError: (error: any) => {
          setPendingMessages(prev => prev.map(m =>
            m.id === pending.id ? { ...m, status: 'failed', error: error.message || "Failed to send message" } : m
          ));
        },
      }
    );
  }, [spaceId, sendMessageMutation, appendMessage]);

  const markAsReadMutation = useMutation({
    mutationFn: async () => {
      return apiRequest("POST", `/api/chat/spaces/${encodeURIComponent(spaceId)}/read`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/chat/spaces"] });
      queryClient.invalidateQueries({ queryKey: ["/api/chat/unread-count"] });
    },
  });

  const reactMessageMutation = useMutation({
    mutationFn: async (data: { messageId: string; emoji: string }) => {
      return apiRequest("POST", `/api/chat/spaces/${encodeURIComponent(spaceId)}/messages/${encodeURIComponent(data.messageId)}/react`, { emoji: data.emoji });
    },
  });

  const [pendingActionId, setPendingActionId] = useState<string | null>(null);

  const approveActionMutation = useMutation({
    mutationFn: (id: string) =>
      apiRequest("POST", `/api/chat/pending-actions/${encodeURIComponent(id)}/approve`),
    onMutate: (id: string) => {
      setPendingActionId(id);
      // Optimistic update via paginated messages state
      messages.forEach((msg: any) => {
        if (msg.metadata?.pendingToolCallId === id) {
          updateMessage(msg.id, (m) => ({ ...m, metadata: { ...m.metadata, status: 'approved' } }));
        }
      });
    },
    onSettled: () => {
      setPendingActionId(null);
    },
  });

  const declineActionMutation = useMutation({
    mutationFn: (id: string) =>
      apiRequest("POST", `/api/chat/pending-actions/${encodeURIComponent(id)}/decline`),
    onMutate: (id: string) => {
      setPendingActionId(id);
      messages.forEach((msg: any) => {
        if (msg.metadata?.pendingToolCallId === id) {
          updateMessage(msg.id, (m) => ({ ...m, metadata: { ...m.metadata, status: 'declined' } }));
        }
      });
    },
    onSettled: () => {
      setPendingActionId(null);
    },
  });

  const clearMemoryMutation = useMutation({
    mutationFn: () => apiRequest('POST', `/api/chat/spaces/${encodeURIComponent(spaceId)}/clear-memory`),
    onSuccess: () => {
      // usePaginatedMessages does not use React Query, so invalidateQueries is a no-op here.
      // Call pollNow() to immediately fetch the sentinel so the UI reflects the memory clear.
      pollNow();
    },
  });

  const cancelGenerationMutation = useMutation({
    mutationFn: () => apiRequest('POST', `/api/chat/spaces/${encodeURIComponent(spaceId)}/cancel`),
    onSuccess: () => {
      setStreamingBotText('');
      setStreamingAgentId(null);
    },
  });

  const displayMessages = useMemo(() => {
    const lastClearIdx = messages.reduce(
      (acc: number, m: any, i: number) => (m.messageType === 'memory_cleared' ? i : acc),
      -1,
    );
    return lastClearIdx >= 0 ? messages.slice(lastClearIdx) : messages;
  }, [messages]);

  const [typingAgents, setTypingAgents] = useState(new Map());
  const typingTimeoutsRef = useRef(new Map());
  const [streamingBotText, setStreamingBotText] = useState('');
  const [streamingAgentId, setStreamingAgentId] = useState<string | null>(null);

  // Local bot typing state: shown 500ms after user sends to an agent DM,
  // cleared when real typing/response arrives. botTimedOut shown after 90s.
  const [waitingForBot, setWaitingForBot] = useState(false);
  const [botTimedOut, setBotTimedOut] = useState(false);
  const typingDelayRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const botTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isAgentDMRef = useRef(false);
  const agentDisplayNameRef = useRef('Chip');
  // Tracks the most recent non-user message id seen by the polling path.
  // Used to detect new bot messages when WebSocket delivery is missed.
  const lastSeenBotMsgIdRef = useRef<string | null>(null);

  const clearBotWait = useCallback(() => {
    if (typingDelayRef.current) { clearTimeout(typingDelayRef.current); typingDelayRef.current = null; }
    if (botTimeoutRef.current) { clearTimeout(botTimeoutRef.current); botTimeoutRef.current = null; }
    setWaitingForBot(false);
    setBotTimedOut(false);
  }, []);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (typingDelayRef.current) clearTimeout(typingDelayRef.current);
      if (botTimeoutRef.current) clearTimeout(botTimeoutRef.current);
    };
  }, []);

  // Fallback: clear bot-wait state when polling delivers a bot message
  // (covers the case where the WebSocket chat:message event was missed).
  useEffect(() => {
    if (!messages || messages.length === 0) return;
    // Find the most recent message from a non-current-user sender.
    const latestBotMsg = [...messages]
      .reverse()
      .find((m: any) => m.senderId !== currentUserId);
    if (!latestBotMsg) return;
    if (latestBotMsg.id !== lastSeenBotMsgIdRef.current) {
      lastSeenBotMsgIdRef.current = latestBotMsg.id;
      // Always clear streaming bubble when new messages arrive via poll,
      // regardless of waitingForBot state (stream may have been in progress).
      setStreamingBotText('');
      setStreamingAgentId(null);
      if (!waitingForBot) return;
      clearBotWait();
      typingTimeoutsRef.current.forEach(t => clearTimeout(t));
      typingTimeoutsRef.current.clear();
      setTypingAgents(new Map());
    }
  }, [messages, waitingForBot, currentUserId, clearBotWait]);

  const handleTyping = useCallback(({ agentId, agentName, isTyping }) => {
    if (isTyping) {
      // Real typing event — cancel the 500ms local delay and clear local indicator
      if (typingDelayRef.current) { clearTimeout(typingDelayRef.current); typingDelayRef.current = null; }
      setWaitingForBot(false);
      setTypingAgents(prev => new Map(prev).set(agentId, agentName));
      const existing = typingTimeoutsRef.current.get(agentId);
      if (existing) clearTimeout(existing);
      typingTimeoutsRef.current.set(agentId, setTimeout(() => {
        setTypingAgents(prev => { const n = new Map(prev); n.delete(agentId); return n; });
      }, 30000));
    } else {
      const existing = typingTimeoutsRef.current.get(agentId);
      if (existing) { clearTimeout(existing); typingTimeoutsRef.current.delete(agentId); }
      setTypingAgents(prev => { const n = new Map(prev); n.delete(agentId); return n; });
      // Also clear the local optimistic indicator
      clearBotWait();
      // Also clear any stale streaming state for this agent
      setStreamingAgentId(prev => (prev === agentId ? null : prev));
      setStreamingBotText(prev => (streamingAgentId === agentId ? '' : prev));
    }
  }, [clearBotWait, streamingAgentId]);

  const handleToken = useCallback(({ agentId, token }: { agentId: string; token: string }) => {
    setStreamingAgentId(prev => prev === agentId ? prev : agentId);
    setStreamingBotText(prev => prev + token);
    // While streaming, hide the typing indicator (functional guard avoids redundant updates)
    setWaitingForBot(prev => prev ? false : prev);
    setTypingAgents(prev => { const n = new Map(prev); n.delete(agentId); return n; });
  }, []);

  const handleNewMessage = useCallback((_msgSpaceId: string, senderId: string) => {
    if (senderId !== currentUserId) {
      // Only count as unread if window is minimized
      if (isMinimized) {
        setLocalUnread(prev => prev + 1);
      } else {
        // Window is open — auto-scroll to show the new message
        userScrolledUpRef.current = false;
      }
      clearBotWait();
      typingTimeoutsRef.current.forEach(t => clearTimeout(t));
      typingTimeoutsRef.current.clear();
      setTypingAgents(new Map());
      setStreamingBotText('');
      setStreamingAgentId(null);
      // Immediately fetch the new message so it appears without waiting for poll
      pollNow();
    }
  }, [currentUserId, clearBotWait, isMinimized, pollNow]);

  useChatWebSocket([spaceId], handleTyping, handleNewMessage, handleToken);

  // Mark as read when window is opened (not minimized)
  useEffect(() => {
    if (hasUnknown) return;
    if (!isMinimized) {
      markAsReadMutation.mutate();
      setLocalUnread(0);
    }
  }, [isMinimized, spaceId, hasUnknown]);

  // Initial scroll: jump to unread banner or bottom
  useEffect(() => {
    if (hasUnknown || isLoadingInitial || messages.length === 0) return;
    if (initialScrollDone.current) return;
    initialScrollDone.current = true;

    // If there are unread messages, scroll to the unread banner
    if (unreadAnchorIndex !== null && unreadBannerRef.current) {
      unreadBannerRef.current.scrollIntoView({ block: "start" });
    } else {
      // All caught up — scroll to bottom
      messagesEndRef.current?.scrollIntoView();
    }
  }, [messages, isLoadingInitial, hasUnknown, unreadAnchorIndex]);

  // Reset initial scroll flag when spaceId changes
  useEffect(() => {
    initialScrollDone.current = false;
  }, [spaceId]);

  // Auto-scroll during streaming (streamingBotText grows but messages list hasn't grown yet)
  useEffect(() => {
    if (!initialScrollDone.current || userScrolledUpRef.current) return;
    if (!streamingBotText) return;
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [streamingBotText]);

  // Auto-scroll to bottom for new messages (only if user hasn't scrolled up)
  const prevMsgCountRef = useRef(0);
  useEffect(() => {
    if (hasUnknown || !initialScrollDone.current) return;
    // Only auto-scroll if new messages were appended (not prepended via loadOlder)
    if (messages.length > prevMsgCountRef.current && !userScrolledUpRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
    prevMsgCountRef.current = messages.length;
  }, [messages, pendingMessages, hasUnknown]);

  // Preserve scroll position when older messages are prepended
  useEffect(() => {
    if (!isLoadingOlder && prevScrollHeightRef.current > 0 && messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      const newScrollHeight = container.scrollHeight;
      container.scrollTop += newScrollHeight - prevScrollHeightRef.current;
      prevScrollHeightRef.current = 0;
    }
  }, [messages, isLoadingOlder]);

  // Detect scroll position: user scrolled up vs at bottom
  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    userScrolledUpRef.current = distanceFromBottom > 80;
  }, []);

  // IntersectionObserver to load older messages when scrolling to top
  useEffect(() => {
    const sentinel = topSentinelRef.current;
    const container = messagesContainerRef.current;
    if (!sentinel || !container || !hasOlder) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasOlder && !isLoadingOlder) {
          // Save scroll height before prepending
          prevScrollHeightRef.current = container.scrollHeight;
          loadOlder();
        }
      },
      { root: container, threshold: 0.1 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasOlder, isLoadingOlder, loadOlder]);

  if (hasUnknown) return null;

  const spaceObj = space?.space || space;
  const isAgentDM = participants?.some((p: any) => p.agentId);

  // Keep refs in sync so handleSend can read them without stale closures
  useEffect(() => { isAgentDMRef.current = isAgentDM; }, [isAgentDM]);

  const quickActions = useMemo(() => {
    if (!isAgentDM) return [];
    return spaceObj?.claimId ? CLAIM_QUICK_ACTIONS : GENERIC_QUICK_ACTIONS;
  }, [isAgentDM, spaceObj?.claimId]);
  const isDM = spaceObj?.spaceType === 'DIRECT_MESSAGE';
  const spaceName = getChatSpaceDisplayName(spaceObj || {}, participants, currentUserId);
  const agentMentions = participants
    .filter((p: any) => p.isAgent)
    .map((p: any) => ({ id: p.agentId, displayName: p.displayName }));

  // Keep agent display name ref in sync
  useEffect(() => {
    agentDisplayNameRef.current = agentMentions[0]?.displayName || 'Chip';
  }, [agentMentions[0]?.displayName]);
  const rightOffset = 24 + index * 340;

  return (
    <div
      className={`${popupMode ? "flex flex-col h-full w-full" : "fixed z-50"} bg-background border shadow-xl flex flex-col transition-all duration-200 ${isFullscreen ? "rounded-none" : "rounded-lg"}`}
      style={popupMode ? { height: "100%", width: "100%" } : isFullscreen ? {
        top: 0, left: 0, right: 0, bottom: 0,
        width: "100dvw", height: "100dvh",
      } : {
        bottom: 16,
        right: rightOffset,
        width: 320,
        height: isMinimized ? 48 : 440,
      }}
      data-testid={`floating-chat-${spaceId}`}
    >
      {/* Header */}
      <div
        className={`flex items-center justify-between px-3 py-2 border-b bg-muted/50 cursor-pointer shrink-0 ${isFullscreen ? "rounded-none" : "rounded-t-lg"}`}
        onClick={() => { if (!isFullscreen) toggleMinimize(spaceId); }}
      >
        <span className="font-medium text-sm truncate flex items-center gap-1.5 min-w-0">
          <span className="truncate">{spaceName}</span>
          {isMinimized && localUnread > 0 && (
            <span className="h-4 min-w-4 px-0.5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center shrink-0">
              {localUnread > 99 ? '99+' : localUnread}
            </span>
          )}
        </span>
        <div className="flex items-center gap-1">
          {streamingBotText && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-destructive"
              title="Stop generating"
              onClick={(e) => { e.stopPropagation(); cancelGenerationMutation.mutate(); }}
              data-testid="button-stop-generation-float"
            >
              <Square className="h-3.5 w-3.5 fill-current" />
            </Button>
          )}
          {isAgentDM && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  title="Clear Memory"
                  onClick={(e) => e.stopPropagation()}
                  disabled={clearMemoryMutation.isPending}
                >
                  <Brain className="h-3.5 w-3.5" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Clear Memory?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will reset the AI's memory and hide previous messages from view. This cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={() => clearMemoryMutation.mutate()}>Clear Memory</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          {!isFullscreen && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={(e) => { e.stopPropagation(); toggleMinimize(spaceId); }}
              data-testid={`floating-chat-minimize-${spaceId}`}
            >
              {isMinimized ? <Maximize2 className="h-3.5 w-3.5" /> : <Minus className="h-3.5 w-3.5" />}
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={(e) => {
              e.stopPropagation();
              setIsFullscreen((prev) => !prev);
              if (isMinimized) toggleMinimize(spaceId);
            }}
            data-testid={`floating-chat-fullscreen-${spaceId}`}
            title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
          >
            {isFullscreen ? <Minimize className="h-3.5 w-3.5" /> : <Maximize className="h-3.5 w-3.5" />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={(e) => { e.stopPropagation(); closeChat(spaceId); }}
            data-testid={`floating-chat-close-${spaceId}`}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Body */}
      {!isMinimized && (
        <>
          {messages.length === 0 && pendingMessages.length === 0 && quickActions.length > 0 && (
            <div className="flex flex-wrap gap-1.5 px-3 py-2" data-testid="floating-chat-quick-actions">
              {quickActions.map((action) => (
                <button
                  key={action.label}
                  className="text-xs px-2.5 py-1.5 rounded-full border border-border bg-muted/50 hover:bg-muted text-foreground transition-colors"
                  onClick={() => handleSend({ text: action.prompt, mentions: [] })}
                  data-testid={`quick-action-${action.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}
          <div
            ref={messagesContainerRef}
            onScroll={handleScroll}
            className="flex-1 overflow-y-auto overflow-x-hidden p-3 space-y-2"
            data-testid={`floating-chat-messages-${spaceId}`}
          >
            {/* Top sentinel for loading older messages */}
            <div ref={topSentinelRef} className="h-1" />
            {isLoadingOlder && (
              <div className="flex justify-center py-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
              </div>
            )}
            {hasOlder && !isLoadingOlder && (
              <div className="flex justify-center">
                <button
                  onClick={() => {
                    if (messagesContainerRef.current) prevScrollHeightRef.current = messagesContainerRef.current.scrollHeight;
                    loadOlder();
                  }}
                  className="text-xs text-muted-foreground hover:text-foreground py-1"
                >
                  Load older messages
                </button>
              </div>
            )}
            {isLoadingInitial && (
              <div className="flex justify-center py-8">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
              </div>
            )}
            {displayMessages.map((message: any, i: number) => {
              if (message.messageType === 'memory_cleared') {
                return (
                  <div key={message.id} className="flex items-center gap-2 py-2 text-xs text-muted-foreground select-none">
                    <div className="h-px flex-1 bg-border" />
                    <span>Memory cleared</span>
                    <div className="h-px flex-1 bg-border" />
                  </div>
                );
              }
              const showSeparator =
                i === 0 ||
                formatMessageDate(message.createdAt) !== formatMessageDate(displayMessages[i - 1].createdAt);
              const showUnreadBanner = unreadAnchorIndex !== null && i === unreadAnchorIndex;
              return (
                <Fragment key={message.id}>
                  {showUnreadBanner && (
                    <div ref={unreadBannerRef} className="flex items-center gap-2 py-1">
                      <div className="flex-1 border-t border-red-400" />
                      <span className="text-[10px] font-medium text-red-500 uppercase tracking-wide">New messages</span>
                      <div className="flex-1 border-t border-red-400" />
                    </div>
                  )}
                  {showSeparator && <ChatDateSeparator date={message.createdAt} />}
                  <MessageBubble
                    message={message}
                    isCurrentUser={message.senderId === currentUserId}
                    currentUserId={currentUserId}
                    agents={agentMentions}
                    onApprove={(id) => approveActionMutation.mutate(id)}
                    onDecline={(id) => declineActionMutation.mutate(id)}
                    isActionLoading={pendingActionId === message.metadata?.pendingToolCallId &&
                      (approveActionMutation.isPending || declineActionMutation.isPending)}
                    reactions={message.reactions}
                    onReact={(messageId, emoji) => reactMessageMutation.mutate({ messageId, emoji })}
                    onClearMemory={isAgentDM ? () => clearMemoryMutation.mutate() : undefined}
                    isClearMemoryPending={clearMemoryMutation.isPending}
                  />
                </Fragment>
              );
            })}
            {pendingMessages.map(pending => (
              <MessageBubble
                key={pending.id}
                message={{
                  id: pending.id,
                  senderId: currentUserId ?? '',
                  text: pending.text,
                  createdAt: new Date(),
                  mentions: pending.mentions,
                  attachments: null,
                  editedAt: null,
                  sender: null,
                }}
                isCurrentUser={true}
                subtitleOverride={
                  pending.status === 'failed'
                    ? (
                        <span className="flex items-center gap-1">
                          <span className="text-destructive">{pending.error || 'Failed'}</span>
                          <button
                            onClick={() => retryMessage(pending)}
                            className="flex items-center gap-0.5 text-primary hover:underline"
                          >
                            <RefreshCw className="h-3 w-3" />
                            Retry
                          </button>
                        </span>
                      )
                    : pending.status === 'uploading'
                      ? 'Uploading...'
                      : 'Sending'
                }
              />
            ))}
            {streamingBotText && streamingAgentId && (() => {
              const streamingAgent = agentMentions.find((a: any) => a.id === streamingAgentId);
              return (
                <MessageBubble
                  key="streaming-bot"
                  message={{
                    id: 'streaming-bot',
                    senderId: streamingAgentId,
                    text: streamingBotText,
                    createdAt: new Date(),
                    mentions: null,
                    attachments: null,
                    editedAt: null,
                    sender: { firstName: streamingAgent?.displayName || 'Chip', lastName: '', email: '' },
                  }}
                  isCurrentUser={false}
                  agents={agentMentions}
                  subtitleOverride={<span className="opacity-50">▋</span>}
                />
              );
            })()}
            <TypingIndicator
              agentNames={
                typingAgents.size > 0
                  ? Array.from(typingAgents.values())
                  : waitingForBot
                    ? [agentDisplayNameRef.current]
                    : []
              }
            />
            <div ref={messagesEndRef} />
          </div>

          {/* Bot timeout error banner */}
          {botTimedOut && (
            <div className="mx-3 mb-2 p-2 rounded-md bg-destructive/10 border border-destructive/20 text-xs text-destructive flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
              <span className="flex-1">{agentDisplayNameRef.current} hasn't responded. The assistant may be unavailable — try again.</span>
              <button
                onClick={() => setBotTimedOut(false)}
                className="shrink-0 text-destructive/70 hover:text-destructive"
                aria-label="Dismiss"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          )}

          {/* Footer */}
          <div className="p-2 border-t shrink-0">
            <MentionInput
              resizable
              enableClaimMention
              participants={participants.filter((p: any) => !p.isAgent)}
              agents={agentMentions}
              onSend={handleSend}
              placeholder="Type a message..."
            />
          </div>
        </>
      )}
    </div>
  );
}
